import React from 'react';

const CactusProposalTemplate: React.FC = () => {
  return (
    <div className="p-8 max-w-4xl mx-auto font-sans">
      {/* Header */}
      <div className="mb-10 text-center border-b border-indigo-100 pb-6">
        <h1 className="text-3xl font-serif font-medium text-indigo-950 mb-2">CACTUS x NEXT GENERATION MEDICINE</h1>
        <p className="text-xl text-indigo-700 font-light">Clinical Product Strategy for the Institute for Healthier Living (IHL), Abu Dhabi</p>
      </div>

      {/* Overview Section */}
      <div className="mb-8">
        <h2 className="text-2xl font-serif font-medium text-indigo-950 mb-4">Overview</h2>
        <p className="text-gray-800 mb-4 text-sm leading-relaxed">
          I am honored to support Cactus on the Institute for Healthier Living (IHL) Abu Dhabi—a visionary initiative at the intersection of clinical care, digital innovation, and longevity science. This project aligns seamlessly with my professional trajectory and expertise.
        </p>
        <p className="text-gray-800 mb-6 text-sm leading-relaxed">
          Over the past decade, I have led clinical product strategy at scale—launching and expanding one of the first national longevity clinics in the U.S., developing software and clinical protocols at Virta Health, and advising ventures like Superpower and Midi Health. These experiences have endowed me with a unique, world-class perspective that bridges clinical excellence, digital product innovation, and strategic foresight—an integration that is rare in the field.
        </p>
      </div>

      {/* Strategic Focus Areas Section */}
      <div className="mb-10">
        <h2 className="text-2xl font-serif font-medium text-indigo-950 mb-4">Strategic Focus Areas for IHL</h2>
        
        <p className="text-gray-800 mb-4 text-sm leading-relaxed">
          In this engagement, I will serve as a clinical product strategist embedded within the Cactus team, ensuring the integrity, clarity, and usability of the digital tools developed across the IHL ecosystem. My contributions will encompass:
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-indigo-50 border-l-4 border-indigo-900 p-6">
            <h3 className="text-lg font-medium text-indigo-950 mb-3">Clinical & Diagnostic Architecture</h3>
            <ul className="space-y-2 text-sm text-gray-800">
              <li className="flex items-start">
                <span className="text-indigo-700 mr-2">•</span>
                <span>Reviewing and shaping core clinical and diagnostic inputs, including test panels, segmentation frameworks, and patient story constructs.</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-indigo-50 border-l-4 border-indigo-900 p-6">
            <h3 className="text-lg font-medium text-indigo-950 mb-3">Product & Tooling Design</h3>
            <ul className="space-y-2 text-sm text-gray-800">
              <li className="flex items-start">
                <span className="text-indigo-700 mr-2">•</span>
                <span>Advising on structuring patient- and clinician-facing tools (e.g., in-room visualizations, Story Studio, clinical intake).</span>
              </li>
              <li className="flex items-start">
                <span className="text-indigo-700 mr-2">•</span>
                <span>Providing asynchronous feedback on wireframes, feature maps, and narrative logic.</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-indigo-50 border-l-4 border-indigo-900 p-6">
            <h3 className="text-lg font-medium text-indigo-950 mb-3">Narrative & Experience Logic</h3>
            <ul className="space-y-2 text-sm text-gray-800">
              <li className="flex items-start">
                <span className="text-indigo-700 mr-2">•</span>
                <span>Assisting the team in prioritizing Day 1 builds across the app, visualization, and care team tooling.</span>
              </li>
              <li className="flex items-start">
                <span className="text-indigo-700 mr-2">•</span>
                <span>Acting as a clinical voice in internal sprint planning and strategic decision sessions.</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-indigo-50 border-l-4 border-indigo-900 p-6">
            <h3 className="text-lg font-medium text-indigo-950 mb-3">Sprint & Stakeholder Collaboration</h3>
            <ul className="space-y-2 text-sm text-gray-800">
              <li className="flex items-start">
                <span className="text-indigo-700 mr-2">•</span>
                <span>Participating in select client-facing meetings to facilitate approvals and alignment.</span>
              </li>
            </ul>
          </div>
        </div>
        
        <p className="text-gray-800 mb-4 text-sm leading-relaxed">
          All work will be coordinated through your internal Cactus lead(s), with my role functioning as a fully integrated (yet discreet) member of the Cactus team.
        </p>
      </div>

      {/* Engagement Model Section */}
      <div className="mb-10">
        <h2 className="text-2xl font-serif font-medium text-indigo-950 mb-4">Engagement Model: Monthly Retainer</h2>
        
        <div className="bg-indigo-900 text-white rounded-sm p-6 mb-6">
          <p className="text-xl font-medium mb-1">Fee: $15,000/month</p>
        </div>

        <p className="text-gray-800 mb-2 text-sm font-medium">Includes:</p>
        <ul className="space-y-2 pl-4 mb-6 text-sm text-gray-800">
          <li className="flex items-start">
            <span className="text-indigo-700 mr-2">•</span>
            <span>1–2 core syncs/week (strategy, product, or design sessions)</span>
          </li>
          <li className="flex items-start">
            <span className="text-indigo-700 mr-2">•</span>
            <span>Unlimited Slack/Notion-based asynchronous input</span>
          </li>
          <li className="flex items-start">
            <span className="text-indigo-700 mr-2">•</span>
            <span>Additional syncs can be scheduled as needed</span>
          </li>
          <li className="flex items-start">
            <span className="text-indigo-700 mr-2">•</span>
            <span>Rolling prioritization of advisory needs by Cactus</span>
          </li>
        </ul>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="border border-gray-200 p-5 rounded-sm">
            <p className="text-indigo-950 font-medium mb-1 text-sm">Duration</p>
            <p className="text-gray-800 text-sm">Month-to-month, with 30-day notice for cancellation or adjustment</p>
          </div>
          
          <div className="border border-gray-200 p-5 rounded-sm">
            <p className="text-indigo-950 font-medium mb-1 text-sm">Start Date</p>
            <p className="text-gray-800 text-sm">Flexible, ideally within the next two weeks</p>
          </div>
        </div>
      </div>

      {/* Value-Driven Partnership Section */}
      <div className="mb-10">
        <h2 className="text-2xl font-serif font-medium text-indigo-950 mb-4">Value-Driven Partnership</h2>
        <p className="text-gray-800 mb-4 text-sm leading-relaxed">
          I approach this collaboration not in terms of hours committed, but value delivered. My work integrates expertise across clinical medicine, product development, and strategic leadership—delivering a level of cross-disciplinary synergy that helps build something truly special. This means applying deep knowledge across multiple domains simultaneously, providing insights and solutions that wouldn't be possible through traditional siloed approaches. My commitment is to the success of your vision, regardless of conventional time constraints.
        </p>
      </div>

      {/* Next Steps Section */}
      <div className="mb-8 border-t border-gray-200 pt-8">
        <h2 className="text-2xl font-serif font-medium text-indigo-950 mb-4">Next Steps</h2>
        <p className="text-gray-800 mb-4 text-sm leading-relaxed">
          If this model aligns with your needs, I am prepared to commence onboarding immediately. I welcome any feedback or suggestions you may have regarding this proposal and am open to discussing adjustments to better suit your objectives. I look forward to collaborating on this world-class initiative.
        </p>
      </div>

      {/* Footer */}
      <div className="mt-12 pt-6 border-t border-gray-200">
        <p className="text-indigo-950 font-medium">Anant Vinjamoori, MD MBA</p>
        <p className="text-gray-800 text-sm">Founder, Next Generation Medicine</p>
                        <p className="text-gray-800 text-sm">anant@nextgenerationmedicine.co</p>
        <a href="https://www.nextgenerationmedicine.co" className="text-indigo-700 hover:text-indigo-900 text-sm" target="_blank" rel="noopener noreferrer">
          www.nextgenerationmedicine.co
        </a>
      </div>
    </div>
  );
};

export default CactusProposalTemplate;